import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {catchError, filter, NEVER, Subscription} from "rxjs";
import {verifiserErIkkeNil, verifiserErIkkeTomListe} from "felles-ng2/støttefunksjoner/antakelser";
import {tap} from "rxjs/operators";
import {Debugger} from "debug";
import {EntitetLager} from "felles-ng2/tjenester/datalager/datatjeneste";
import {nil} from "felles-ng2/typer/nullish";
import {Modelloppdatering, ModelloppdateringType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {inspiser} from "felles-ng2/støttefunksjoner/lister";
import {tomFunksjon} from "felles-ng2/støttefunksjoner/funksjoner";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";

/** Basisklasse for håndtering av modelloppdatering-push meldinger */
export abstract class ModellHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, M extends ModellHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, M>> {
    private _hendelseType!: ModelloppdateringType;
    private _ignorerAbonnentSjekk = false;
    private _abonnentoppslag: AbonnenterOgNøkkelFn<HENDELSE_MODELL_TYPE>[] = [];
    private _filter: FilterFn<HENDELSE_MODELL_TYPE> = () => true;
    private _førLagerOppdatert: InspiserModelloppdatering<HENDELSE_MODELL_TYPE> = tomFunksjon;
    private _etterLagerOppdatert: InspiserModelloppdatering<HENDELSE_MODELL_TYPE> = tomFunksjon;

    private _entitetId!: (hendelseModell: HENDELSE_MODELL_TYPE) => EnEllerFlere<string>;

    constructor(protected readonly datalager: EntitetLager<any>, protected readonly modelloppdateringer: Modelloppdateringer, private readonly _debug: Debugger) {
    }

    lyttTil(hendelseType: ModelloppdateringType): M {
        this._hendelseType = hendelseType;
        return this as any as M;
    }

    get hendelseType() {
        return this._hendelseType;
    }

    /** metode som benyttes for å lese entitetId fra hendelseModell.
     * Benyttes når entitet skal fjernes fra lokallager ifbm entitet-slettet hendelser eller
     * i logmelding når lasting av entitet feilet pga ikke-tilgang / eksisterer-ikke */
    medEntitetId(entitetId: (hendelseModell: HENDELSE_MODELL_TYPE) => EnEllerFlere<string>): M {
        this._entitetId = entitetId;
        return this as any as M;
    }

    get entitetId(): (hendelseModell: HENDELSE_MODELL_TYPE) => EnEllerFlere<string> {
        return this._entitetId;
    }

    /** Ikke gjør oppslag mot eventuelle abonnenter.
     * Dette medfører at {@link #oppdaterLagerBasertPåHendelse} vil bli kalt betingelsesløst for modelloppdatering-hendelser av angitt {@link hendelseType} */
    medIgnorerAbonnementSjekk() {
        this._ignorerAbonnentSjekk = true;
        return this as any as M;
    }

    get ignorerAbonnentSjekk(): boolean {
        return this._ignorerAbonnentSjekk;
    }

    /** Ved modelloppdatering-hendelse oppdaterer vi datalager dersom angitt abonnenter inneholder et abonnement på nøkkel returnert av angitt nøkkelFn.
     * Denne metoden kan kalles multiple ganger for å registrere flere abonnent-kriterier.
     * Dersom {@link #medIgnorerAbonnementSjekk()} har blitt kalt tidligere vil denne resettes til false.
     * Merk at abonnentoppslag vil bli utført ETTER at eventuelt filter registrert via {@link #medFilter} har blitt evaluert. */
    medAbonnentoppslag(abonnenter: DatalagerAbonnenter, nøkkelFn: NøkkelFn<HENDELSE_MODELL_TYPE>): M {
        this._abonnentoppslag.push({abonnenter: abonnenter, nøkkelFn: nøkkelFn});
        this._ignorerAbonnentSjekk = false;
        return this as any as M;
    }

    /** Ved modelloppdatering-hendelse oppdaterer vi datalager dersom angitt abonnenter inneholder et abonnement på nøkkel returnert av angitt nøkkelFn.
     * I motsetning til {@link #medAbonnentoppslag} tolererer vi at angitt nøkkelFn returnerer nil.
     * Denne metoden kan kalles multiple ganger for å registrere flere abonnent-kriterier.
     * Dersom {@link #medIgnorerAbonnementSjekk()} har blitt kalt tidligere vil denne resettes til false.*/
    medAbonnentoppslagOpt(abonnenter: DatalagerAbonnenter, nøkkelOptFn: NøkkelOptFn<HENDELSE_MODELL_TYPE>): M {
        return this.medAbonnentoppslag(abonnenter, this.nøkkelOptSomNøkkelFn(nøkkelOptFn))
    }

    /** Dersom angitt metode returnerer false blir hendelsen ignorert.
     * Filter vil bli evaluert FØR abonnentoppslag registrert via {@link #medAbonnentoppslag}/{@link medAbonnenterOgNøkkelSamling} blir evaluert.*/
    medFilter(filter: FilterFn<HENDELSE_MODELL_TYPE>): M {
        this._filter = filter;
        return this as any as M;
    }

    /** Setter abonnement oppslag samlingen til angitt samling.
     * Merk at eventuelle tidligere registrerte abonnementOppslag vil blir overskrevet.
     * Dersom {@link #medIgnorerAbonnementSjekk()} har blitt kalt tidligere vil denne resettes til false.
     * Merk at abonnentoppslag vil bli utført ETTER at eventuelt filter registrert via {@link #medFilter} har blitt evaluert. */
    medAbonnenterOgNøkkelSamling(abonnenterOgNøkler: AbonnenterOgNøkkelFn<HENDELSE_MODELL_TYPE>[]): M {
        this._abonnentoppslag = [...abonnenterOgNøkler];
        this._ignorerAbonnentSjekk = false;
        return this as any as M;
    }

    get abonnentoppslag(): AbonnenterOgNøkkelFn<HENDELSE_MODELL_TYPE>[] {
        return [...this._abonnentoppslag];
    }

    get filter(): FilterFn<HENDELSE_MODELL_TYPE> {
        return this._filter;
    }

    medFørLagerOppdatert(førLagerOppdatert: InspiserModelloppdatering<HENDELSE_MODELL_TYPE>): M {
        this._førLagerOppdatert = førLagerOppdatert;
        return this as any as M;
    }

    get førLagerOppdatert(): InspiserModelloppdatering<HENDELSE_MODELL_TYPE> {
        return this._førLagerOppdatert;
    }

    medEtterLagerOppdatert(etterLagerOppdatert: InspiserModelloppdatering<HENDELSE_MODELL_TYPE>): M {
        this._etterLagerOppdatert = etterLagerOppdatert;
        return this as any as M;
    }

    get etterLagerOppdatert(): InspiserModelloppdatering<HENDELSE_MODELL_TYPE> {
        return this._etterLagerOppdatert;
    }

    /** Starter å lytte til endringshendelser og oppdaterer lager når slike opptrer.
     * Merk at denne metoden oppretter en kopi av instansen den kalles på, og lytteren
     * blir så initiert av denne kopien. */
    oppdaterLagerVedHendelse(): Subscription {
        return this.kopier().utførOppdaterLagerVedHendelse();
    }

    //public for testbarhet
    public kopier(): M {
        const kopi = this.opprettKopi()
          .lyttTil(this.hendelseType)
          .medEntitetId(this.entitetId)
          .medFilter(this.filter)
          .medFørLagerOppdatert(this.førLagerOppdatert)
          .medEtterLagerOppdatert(this.etterLagerOppdatert);
        if (this.ignorerAbonnentSjekk)
            kopi.medIgnorerAbonnementSjekk();
        else
            kopi.medAbonnenterOgNøkkelSamling(this.abonnentoppslag);
        return kopi;
    }

    /** Oppretter og returnerer en kopi av den konkrete ModellHendelseHåndtering */
    protected abstract opprettKopi(): M;

    /** Oppdater lager basert på angitt hendelseModell */
    protected abstract oppdaterLager(hendelseModell: HENDELSE_MODELL_TYPE): Promise<void>;

    protected debug(melding: string, ...argumenter: any[]) {
        this._debug(`${this.hendelseType}: ${melding}`, argumenter);
    }

    //private hjelpemetoder

    private nøkkelOptSomNøkkelFn(nøkkelFn: NøkkelOptFn<HENDELSE_MODELL_TYPE>): NøkkelFn<HENDELSE_MODELL_TYPE> {
        return (hendelseModell: HENDELSE_MODELL_TYPE) => nøkkelFn(hendelseModell) ?? 'null_nøkkel'
    }

    private utførOppdaterLagerVedHendelse(): Subscription {
        verifiserErIkkeNil(this.entitetId, 'har du glemt å kalle medEntitetId(..)?');
        verifiserErIkkeNil(this.hendelseType, 'har du glemt å kalle lyttTil(..)?');
        if (!this.ignorerAbonnentSjekk) {
            verifiserErIkkeTomListe(this.abonnentoppslag, 'har du glemt å kalle medAbonnentNøkkel(..)?');
        }

        return this.modelloppdateringer.hendelser
          .pipe(
            tap(hendelse => this.debug('hendelse', hendelse, this.abonnentoppslag)),
            filter(hendelse => this.filter(hendelse)),
            filter(hendelse => hendelse.type == this.hendelseType),
            filter(hendelse => this.detFinnesAbonnenter(hendelse)),
            tap(hendelse => this.debug(`hendelse fører til lageroppdatering`, hendelse)),
            catchError(feil => this.håndterPipeFeil(feil))
          )
          .subscribe(async hendelse => await this.oppdaterLagerBasertPåHendelse(hendelse));
    }

    private detFinnesAbonnenter(hendelse: Modelloppdatering<any>): boolean {
        if (this.ignorerAbonnentSjekk) return true;
        return this.abonnentoppslag
          .filter(a => a.abonnenter.inneholder(a.nøkkelFn(hendelse.modell)))
          .map(inspiser(a => this.debug(`${a.abonnenter.antallAbonnenter(a.nøkkelFn(hendelse.modell))} abonnenter reg i ${a.abonnenter.navn} for ${a.nøkkelFn(hendelse.modell)}`)))
          .length > 0;
    }

    private async oppdaterLagerBasertPåHendelse(hendelse: Modelloppdatering<any>) {
        try {
            try {
                this.førLagerOppdatert(hendelse);
            } catch (e) {
                console.warn('ModellHendelseHåndteringBygger.oppdaterLagerBasertPåHendelse førLagerOppdatert(..) feilet', e)
            }

            await this.oppdaterLager(hendelse.modell);

            try {
                this.etterLagerOppdatert(hendelse);
            } catch (e) {
                console.warn('ModellHendelseHåndteringBygger.oppdaterLagerBasertPåHendelse etterLagerOppdatert(..) feilet', e)
            }
        } catch (e) {
            this.debug('oppdaterer lager feilet', e, hendelse);
        }
    }

    private håndterPipeFeil(feil: any) {
        this.debug('prosessering av hendelse feilet', feil);
        return NEVER
    }

}

export type EnEllerFlere<TYPE> = TYPE | TYPE[];

export type NøkkelFn<HENDELSE_MODELL_TYPE> = (hendelseModell: HENDELSE_MODELL_TYPE) => string | string[];
export type NøkkelOptFn<HENDELSE_MODELL_TYPE> = (hendelseModell: HENDELSE_MODELL_TYPE) => string | string[] | nil;
export type AbonnenterOgNøkkelFn<HENDELSE_MODELL_TYPE> = {
    abonnenter: DatalagerAbonnenter;
    nøkkelFn: NøkkelFn<HENDELSE_MODELL_TYPE>;
};
export type FilterFn<HENDELSE_MODELL_TYPE> = (modelloppdatering: Modelloppdatering<HENDELSE_MODELL_TYPE>) => boolean;
export type InspiserModelloppdatering<HENDELSE_MODELL_TYPE> = (modelloppdatering: Modelloppdatering<HENDELSE_MODELL_TYPE>) => void;
